<template>
  <div class="float-right boss-batch-select">
    <span
      v-for="(option ,i) in bulkRegisterOptions"
      :key="i"
      v-tooltip="{content: showTooltip(option.limit) ? option.tooltip:''}"
      :data-test="'bulk-button-tooltip-' + option.id"
    >
      <button
        :id="'bulk-button-' + option.id"
        :disabled="isDisabled(option.id, option.limit)"
        :data-test="'bulk-button-' + option.id"
        class="boss-button boss-exe-button pega-button pega-mr-5 pega-bulk-button"
        @click="bulkRegister(option.id)"
      >
        <span class="boss-exe-button-text"> {{ option.name }}</span>
      </button>
    </span>
  </div>
</template>

<script>
import * as definition from '@/lib/labelValueMappings'
import _ from 'lodash'
import { mapMutations, mapGetters } from 'vuex'
import Indicator from '@/lib/indicator'

export default {
  props: {
    jobId: {
      type: Number,
      default: null
    },
    contractId: {
      type: Number,
      default: null
    },
    memberObjects: {
      type: Array,
      default: () => ([])
    },
    searchName: {
      type: String,
      default: ''
    },
    searchConditionId: {
      type: Number,
      default: null
    }
  },
  data () {
    return {
      bulkRegisterOptions: [{ id: '', name: '' }],
      routeNameKeyMap: {
        scoutUnderReview: { apiPath: `/scout/view/scouts/contracts/${this.contractId}/jobs/${this.jobId}/candidates` },
        scoutExcluded: { apiPath: '/scout/view/company/excluded_users' }
      }
    }
  },
  computed: {
    registerObjects () {
      return _(this.memberObjects).map('member').map(m => {
        return {
          userId: m.id,
          searchConditionName: this.searchName ? this.searchName : _.defaultTo(m.searchConditionName, '')
        }
      }).value()
    },
    deleteIds () {
      return _(this.memberObjects).flatMap('id').value()
    },
    newMemberIds () {
      return _(this.memberObjects).map('member').filter('isNew').flatMap('id').value()
    },
    recommendedIds () {
      return _(this.memberObjects).map('member').filter('isNew').flatMap('recommendedId').compact().value()
    },
    isLikeLimitOver () {
      return this.isLikeLimitOverBy(this.contractId)
    },
    ...mapGetters('contracts', [
      'isLikeLimitOverBy',
      'isPublishedBy',
      'canScoutResend'
    ]),
    ...mapGetters([
      'isSendScout'
    ])
  },
  watch: {
    jobId: {
      handler (val) {
        this.setOptions()
        this.$set(this.routeNameKeyMap.scoutUnderReview, 'apiPath', `/scout/view/scouts/contracts/${this.contractId}/jobs/${val}/candidates`)
      }
    },
    contractId (val) {
      this.$set(this.routeNameKeyMap.scoutUnderReview, 'apiPath', `/scout/view/scouts/contracts/${val}/jobs/${this.jobId}/candidates`)
    }
  },
  created () {
    this.setOptions()
  },
  methods: {
    isDisabled (id, limit) {
      return (id === 6 && this.isLikeLimitOver) || // いいね一括かつ残数ゼロなら非活性
      _.isEmpty(this.memberObjects) ||
      this.memberObjects.length > limit
    },
    showTooltip (limit) {
      return !_.isEmpty(this.memberObjects) && this.memberObjects.length > limit
    },
    bulkRegister (id) {
      const executerMap = {
        // 検討中一覧に振り分ける
        1: { func (component) { component.registerCandidates() } },
        // 除外者一覧に振り分ける
        2: { func (component) { component.registerExcludedMembers() } },
        // 検索対象に戻す
        3: { func (component) { component.deleteExcluded() } },
        // スカウト作成画面に遷移
        4: { func (component) { component.goToScoutCreate() } },
        // スカウト再送作成画面に遷移
        5: { func (component) { component.goToScoutCreate() } },
        // いいね
        6: { func (component) { component.registerLikes() } },
        // 過去契約からの送信候補
        7: { func (component) { component.registerCandidatesCopy() } }
      }
      executerMap[Number(id)].func(this)
    },
    fetch () {
      this.$emit('fetch')
    },
    clearCheckedMembers () {
      this.$emit('clearCheckedMembers')
    },
    fetchAndClearCheckedMembers () {
      this.fetch()
      this.clearCheckedMembers()
    },
    setOptions () {
      const options = definition.bulkRegister
        .filter(o => _.includes(o.routeNameKeys, this.$route.name))
        .filter(o => {
          if (o.isPermission && !this.isSendScout) {
            return false
          }
          // 掲載中または再送期限ないか判定するかどうか
          if (o.isPublishedCheck) {
            return this.isPublishedBy(this.contractId, this.jobId) || o.isResend ? this.canScoutResend(this.contractId, this.jobId) : false
          // 送信候補へコピーを表示するかの判定するかどうか
          } else if (o.showCopy) {
            return !this.isPublishedBy(this.contractId, this.jobId) && (!this.canScoutResend(this.contractId, this.jobId) || this.routeNameKeyMap[this.$route.name])
          } else {
            return true
          }
        })
      this.$set(this, 'bulkRegisterOptions', options)
    },
    post (resource, param, callback = '') {
      Indicator.emitShow()
      this.$http.secured.post(resource, param).then((response) => {
        _.isFunction(callback) && callback(response)
        this.fetchAndClearCheckedMembers()
      }).catch(() => {
        // finallyではなくcatch 正常の場合はfetch時にIndicatorは消えるため
        Indicator.emitHide()
      })
    },
    delete (resource, param, callback = '') {
      Indicator.emitShow()
      this.$http.secured.delete(resource, param).then((response) => {
        _.isFunction(callback) && callback(response)
      }).catch(() => {
        Indicator.emitHide()
      })
    },
    beforeDeleteIfNeed (executer, isDelete) {
      if (this.routeNameKeyMap[this.$route.name] && isDelete) {
        this.delete(`${this.routeNameKeyMap[this.$route.name].apiPath}`, { data: this.deleteIds }, executer)
      } else {
        executer()
      }
    },
    register (func = '', isDelete = true) {
      this.beforeDeleteIfNeed(func, isDelete)
    },
    goToScoutCreate () {
      this.$router.push({ name: 'scoutCreate', params: { idAndNameObjects: this.registerObjects, jobId: this.jobId, contractId: this.contractId } })
    },
    showLikeToasted () {
      this.$toasted.global.like()
    },
    showCandidateToasted () {
      this.$toasted.global.success({ message: '送信候補へ移動しました。' })
    },
    showExcludedToasted () {
      this.$toasted.global.success({ message: '除外者一覧へ登録しました。' })
    },
    showDeleteExcludedToasted () {
      this.$toasted.global.success({ message: '検索対象に戻しました。' })
    },
    showCandidateCopyToasted () {
      this.$toasted.global.success({ message: '送信候補にコピーしました(コピー可能な会員のみ)。' })
    },
    registerCandidates () {
      this.register(() => {
        this.post(`/scout/view/scouts/contracts/${this.contractId}/jobs/${this.jobId}/candidates`, this.registerObjects, (response) => {
          this.showCandidateToasted()
          this.updateNewRead()
        })
      })
    },
    registerExcludedMembers () {
      this.register(() => {
        this.post('/scout/view/company/excluded_users', this.registerObjects, (response) => {
          this.showExcludedToasted()
          this.updateNewRead()
        })
      })
    },
    deleteExcluded () {
      this.register((response) => {
        this.fetchAndClearCheckedMembers()
        this.showDeleteExcludedToasted()
      })
    },
    updateNewRead () {
      if (this.searchConditionId && this.newMemberIds.length) {
        this.$http.secured.patch(`scout/view/scouts/search_conditions/${this.searchConditionId}/users/_is_read`, this.newMemberIds)
      } else if (this.recommendedIds.length) {
        this.$http.secured.patch('scout/view/recommends/resume/_is_read', this.recommendedIds)
      }
    },
    registerLikes () {
      this.register(() => {
        this.post(`scout/message/send/scouts/contracts/${this.contractId}/jobs/${this.jobId}/likes`, this.registerObjects, (response) => {
          this.showLikeToasted()
          this.updateNewRead()
          this.fetchLikeAmount({ contractId: this.contractId })
        })
      }, !this.routeNameKeyMap[this.$route.name])
    },
    registerCandidatesCopy () {
      this.register(() => {
        this.post(`/scout/view/scouts/contracts/${this.contractId}/jobs/${this.jobId}/candidates/_copy`, this.registerObjects, (response) => {
          this.showCandidateCopyToasted()
        })
      }, false)
    },
    ...mapMutations('contracts', [
      'fetchLikeAmount'
    ])
  }
}
</script>
